import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import { RootState } from './store';
import APIService from '../Utils/APIService';
import { showAddNewVehicle, showEditVehicle } from './app-ui.store';
import { DEFAULT_PAGE_SIZE } from '../Utils/constants';
import Vehicle from '../Types/Vehicle';
import { fetchMinimalLicenseAndVehicle } from './drivers.store';

export interface VehicleDistance {
  vehicleNumber: string;
  totalDistance: number;
  nof_trips: number;
}

export interface VehicleState {
  loading: boolean;
  error: boolean;
  selectedVehicle: any;
  selectedVehicleLoading: boolean;
  updateTimestamp: number;
  vehicleDistance: VehicleDistance[];
  vehicleDistanceLoading: boolean;
  vehicles: Vehicle[];
  totalVehicles: number;
}

const initialState = {
  vehicles: [],
  loading: false,
  error: false,
  selectedVehicle: {},
  selectedVehicleLoading: false,
  updateTimestamp: 0,
  vehicleDistance: [],
  vehicleDistanceLoading: false,
  totalVehicles: 0,
} as VehicleState;

export const getAllVehicles = createAsyncThunk<any, { page: number; page_size: number }>(
  'vehicles/get-all',
  async (arg, { rejectWithValue }) => {
    const page = Math.floor(arg.page / arg.page_size) + 1;
    const [err, data] = await to(APIService.get('user/vehicle', { params: { page, page_size: arg.page_size } }));
    if (err) {
      toast.error('Something went wrong!. Could not fetch the vehicles');
      return rejectWithValue(err);
    }
    return data?.data;
  },
);

export const addNewVehicle = createAsyncThunk<any, Vehicle>(
  'vehicles/create-new',
  async (vehicle, { rejectWithValue, dispatch }) => {
    const [err, data] = await to(APIService.post('/user/vehicle', vehicle));
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not create the vehicle`);
      return rejectWithValue(err);
    }
    toast.success('Successfully added the vehicle');
    dispatch(showAddNewVehicle(false));
    dispatch(getAllVehicles({ page: 0, page_size: DEFAULT_PAGE_SIZE }));
    return data?.data;
  },
);

export const editVehicle = createAsyncThunk<any, Vehicle>(
  'vehicles/edit-vehicle',
  async (vehicle, { rejectWithValue, dispatch }) => {
    const { vehicle_id, capacity, fuelEfficiency, fuelType, mileage, status, volume, type } = vehicle as any;
    const [err, data] = await to(
      APIService.put(`/user/vehicle/${vehicle_id}`, {
        capacity,
        fuelEfficiency,
        fuelType,
        mileage,
        status,
        volume,
        type,
      }),
    );
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not update the vehicle`);
      return rejectWithValue(err);
    }
    toast.success('Successfully edited the vehicle');
    dispatch(showEditVehicle(null));
    dispatch(getAllVehicles({ page: 0, page_size: DEFAULT_PAGE_SIZE }));
    return data?.data;
  },
);

export const updateVehicleStatus = createAsyncThunk<any, { vehicleId: number; status: string }>(
  'vehicles/update-status',
  async ({ vehicleId, status }, { rejectWithValue, dispatch }) => {
    const [err, data] = await to(APIService.put(`/user/vehicle/${vehicleId}`, { status }));
    if (err) {
      const serverMsg = (err as any)?.response?.data?.message || (err as any)?.message || 'Could not update the status';
      toast.error(serverMsg);
      return rejectWithValue(err);
    }
    toast.success('Successfully status updated');
    dispatch(getAllVehicles({ page: 0, page_size: DEFAULT_PAGE_SIZE }));
    dispatch(fetchMinimalLicenseAndVehicle(null));
    return data?.data;
  },
);

export const getVehicleDistance = createAsyncThunk<VehicleDistance[], void>(
  'vehicles/get-distance',
  async (_, { rejectWithValue }) => {
    const [err, response] = await to(APIService.get('/user/vehicle-distance'));
    if (err) {
      toast.error('Something went wrong!. Could not fetch the vehicle distance');
      return rejectWithValue(err);
    }

    const data: VehicleDistance[] = Object.entries(response?.data || {}).map(
      ([vehicleNumber, info]: [string, any]) => ({
        vehicleNumber,
        totalDistance: info.total_distance,
        nof_trips: info.nof_trips,
      }),
    );
    return data;
  },
);

export const vehicleSlice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    setSelectedVehicleDetails: (store, { payload }) => {
      store.selectedVehicle = payload;
      store.selectedVehicleLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllVehicles.pending, (store) => {
      store.loading = true;
      store.error = false;
    });
    builder.addCase(getAllVehicles.rejected, (store) => {
      store.loading = false;
      store.error = true;
    });
    builder.addCase(getAllVehicles.fulfilled, (store, { payload }) => {
      store.loading = false;
      store.vehicles = Array.isArray(payload) ? payload : payload?.data ?? [];
      store.totalVehicles = Array.isArray(payload) ? payload.length : payload?.total ?? 0;
      store.updateTimestamp = Date.now();
    });
    builder.addCase(getVehicleDistance.pending, (store) => {
      store.vehicleDistanceLoading = true;
      store.error = false;
    });
    builder.addCase(getVehicleDistance.fulfilled, (store, { payload }) => {
      store.vehicleDistanceLoading = false;
      store.vehicleDistance = payload;
    });
    builder.addCase(getVehicleDistance.rejected, (store) => {
      store.vehicleDistanceLoading = false;
      store.error = true;
    });
    builder.addCase(updateVehicleStatus.pending, (store) => {
      store.loading = true;
    });
    builder.addCase(updateVehicleStatus.rejected, (store) => {
      store.loading = false;
    });
  },
});

// export const {  } = vehicleSlice.actions;

export const vehicleStore = (store: RootState): VehicleState => store.vehicle;
export const { setSelectedVehicleDetails } = vehicleSlice.actions;

export const getSelectedVehicleDetails = createSelector(vehicleStore, (store) => store.selectedVehicle);
export const getSelectedVehicleDetailsLoading = createSelector(vehicleStore, (store) => store.selectedVehicleLoading);
export const getVehicleLoading = createSelector(vehicleStore, (store) => store.loading);
export const getVehicleDistanceLoading = createSelector(vehicleStore, (store) => store.vehicleDistanceLoading);
export const getVehicles = createSelector(vehicleStore, (store) => store.vehicles);
export const getVehicleDistanceData = createSelector(vehicleStore, (store) => store.vehicleDistance);
export const getVehicleTotal = createSelector(vehicleStore, (store) => store.totalVehicles);

export default vehicleSlice.reducer;
